import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete, Box, Button, Checkbox, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Popover,
  Select, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignCenterIcon from '@mui/icons-material/VerticalAlignCenter';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import DataObjectIcon from '@mui/icons-material/DataObject';
import {
  SoaAlign, SoaBorderSide, SoaBorderStyle, SoaBorders, SoaFieldDef, SoaFieldType, SoaStyle, SoaVAlign,
} from '../../types/formattedSoa';
import { GROUP_LABELS, GROUP_ORDER, placeholder } from '../../utils/soaDesigner';

// ── number ───────────────────────────────────────────────────────────────────────────────────

interface NumFieldProps {
  label: string;
  value: number | null | undefined;
  onChange: (v: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  /** Empty = undefined (inherit / default). */
  allowEmpty?: boolean;
  disabled?: boolean;
  width?: number | string;
  helperText?: string;
}

/** A number box that lets the user type freely ("1.", "") and reports only valid numbers. */
export const NumField: React.FC<NumFieldProps> = ({
  label, value, onChange, min = -100000, max = 100000, step = 0.5, unit, allowEmpty = false, disabled, width = 92, helperText,
}) => {
  const [text, setText] = useState(value === null || value === undefined ? '' : String(value));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(value === null || value === undefined ? '' : String(value));
  }, [value]);
  const commit = (raw: string) => {
    if (raw.trim() === '') {
      if (allowEmpty) onChange(undefined);
      return;
    }
    const n = Number(raw);
    if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, Math.round(n * 100) / 100)));
  };
  return (
    <TextField
      label={unit ? `${label} (${unit})` : label}
      size="small"
      type="number"
      value={text}
      disabled={disabled}
      helperText={helperText}
      inputProps={{ step, min, max }}
      sx={{ width }}
      onFocus={() => { focused.current = true; }}
      onBlur={() => {
        focused.current = false;
        setText(value === null || value === undefined ? '' : String(value));
      }}
      onChange={(e) => {
        setText(e.target.value);
        commit(e.target.value);
      }}
    />
  );
};

// ── colour ───────────────────────────────────────────────────────────────────────────────────

interface ColorFieldProps {
  label: string;
  value: string | undefined;
  /** undefined (or '' when `emptyValue` is '') = no colour */
  onChange: (v: string | undefined) => void;
  /** Shows a tick box: unticked = no colour (inherit / none). */
  optional?: boolean;
  fallback?: string;
}

export const ColorField: React.FC<ColorFieldProps> = ({ label, value, onChange, optional = false, fallback = '#000000' }) => {
  const on = !!value;
  const id = useMemo(() => `c${Math.random().toString(36).slice(2, 9)}`, []);
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {optional ? (
        <FormControlLabel
          sx={{ mr: 0 }}
          control={<Checkbox size="small" checked={on} onChange={(e) => onChange(e.target.checked ? (value || fallback) : undefined)} />}
          label={<Typography variant="body2">{label}</Typography>}
        />
      ) : (
        <Typography variant="body2" component="label" htmlFor={id} sx={{ minWidth: 62 }}>{label}</Typography>
      )}
      <input
        id={id}
        type="color"
        aria-label={label}
        value={value || fallback}
        disabled={optional && !on}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 34, height: 26, padding: 0, border: '1px solid #c4c9d0', borderRadius: 3, background: 'none', opacity: optional && !on ? 0.35 : 1 }}
      />
    </Stack>
  );
};

// ── borders ──────────────────────────────────────────────────────────────────────────────────

const SIDES = ['top', 'right', 'bottom', 'left'] as const;
const BORDER_STYLES: SoaBorderStyle[] = ['solid', 'dashed', 'dotted', 'double'];

interface BordersEditorProps {
  value: SoaBorders | undefined;
  onChange: (b: SoaBorders | undefined) => void;
}

/** Width (pt), style and colour per side, plus "All sides". Width 0 removes that side. */
export const BordersEditor: React.FC<BordersEditorProps> = ({ value, onChange }) => {
  const b = value ?? {};
  const setSide = (side: typeof SIDES[number], s: SoaBorderSide | undefined) => {
    const next: SoaBorders = { ...b };
    if (!s || s.width <= 0) delete next[side];
    else next[side] = s;
    onChange(Object.keys(next).length ? next : undefined);
  };
  const first = SIDES.map((s) => b[s]).find(Boolean);
  const row = (label: string, current: SoaBorderSide | undefined, apply: (s: SoaBorderSide | undefined) => void) => (
    <Stack direction="row" spacing={0.5} alignItems="center" key={label}>
      <Typography variant="caption" sx={{ width: 44 }}>{label}</Typography>
      <NumField label="pt" value={current?.width ?? 0} min={0} max={10} step={0.25} width={66}
        onChange={(w) => apply(w ? { width: w, style: current?.style ?? 'solid', color: current?.color ?? '#000000' } : undefined)} />
      <Select size="small" value={current?.style ?? 'solid'} sx={{ width: 92 }} inputProps={{ 'aria-label': `${label} border style` }}
        onChange={(e) => apply({ width: current?.width || 0.75, style: e.target.value as SoaBorderStyle, color: current?.color ?? '#000000' })}>
        {BORDER_STYLES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
      </Select>
      <input type="color" aria-label={`${label} border colour`} value={current?.color ?? '#000000'}
        onChange={(e) => apply({ width: current?.width || 0.75, style: current?.style ?? 'solid', color: e.target.value })}
        style={{ width: 30, height: 26, padding: 0, border: '1px solid #c4c9d0', borderRadius: 3 }} />
    </Stack>
  );
  return (
    <Stack spacing={0.75}>
      {row('All', first, (s) => onChange(s && s.width > 0 ? { top: s, right: s, bottom: s, left: s } : undefined))}
      {SIDES.map((side) => row(side[0].toUpperCase() + side.slice(1), b[side], (s) => setSide(side, s)))}
    </Stack>
  );
};

// ── style ────────────────────────────────────────────────────────────────────────────────────

interface StyleEditorProps {
  value: SoaStyle;
  onChange: (s: SoaStyle) => void;
  fonts: string[];
  /** 'text' = every property; 'box' = background, padding and borders only */
  mode?: 'text' | 'box';
  /** Shows "(default)" choices that leave a property to the template / table defaults. */
  inheritLabel?: string;
}

function setKey<K extends keyof SoaStyle>(s: SoaStyle, k: K, v: SoaStyle[K] | undefined): SoaStyle {
  const next: SoaStyle = { ...s };
  if (v === undefined || v === null) delete next[k];
  else next[k] = v;
  return next;
}

export const StyleEditor: React.FC<StyleEditorProps> = ({ value, onChange, fonts, mode = 'text', inheritLabel = 'Default' }) => {
  const s = value ?? {};
  const set = <K extends keyof SoaStyle>(k: K, v: SoaStyle[K] | undefined) => onChange(setKey(s, k, v));
  const flags = ['bold', 'italic', 'underline', 'uppercase'].filter((k) => s[k as keyof SoaStyle] === true);
  return (
    <Stack spacing={1.25}>
      {mode === 'text' && (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
              <InputLabel>Font</InputLabel>
              <Select label="Font" value={s.fontFamily ?? ''} onChange={(e) => set('fontFamily', e.target.value ? String(e.target.value) : undefined)}>
                <MenuItem value=""><em>{inheritLabel}</em></MenuItem>
                {fonts.map((f) => <MenuItem key={f} value={f} sx={{ fontFamily: `"${f}", sans-serif` }}>{f}</MenuItem>)}
              </Select>
            </FormControl>
            <NumField label="Size" unit="pt" value={s.fontSize} allowEmpty min={4} max={72} step={0.5} width={84} onChange={(v) => set('fontSize', v)} />
          </Stack>
          <ToggleButtonGroup
            size="small"
            value={flags}
            aria-label="Text style"
            onChange={(_, next: string[]) => {
              let n = { ...s };
              for (const k of ['bold', 'italic', 'underline', 'uppercase'] as const) n = setKey(n, k, next.includes(k) ? true : undefined);
              onChange(n);
            }}
          >
            <ToggleButton value="bold" aria-label="Bold"><FormatBoldIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="italic" aria-label="Italic"><FormatItalicIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="underline" aria-label="Underline"><FormatUnderlinedIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="uppercase" aria-label="Upper case"><TextFieldsIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <ToggleButtonGroup size="small" exclusive value={s.align ?? null} aria-label="Horizontal alignment"
              onChange={(_, v: SoaAlign | null) => set('align', v ?? undefined)}>
              <ToggleButton value="left" aria-label="Align left"><FormatAlignLeftIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="center" aria-label="Align centre"><FormatAlignCenterIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="right" aria-label="Align right"><FormatAlignRightIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="justify" aria-label="Justify"><FormatAlignJustifyIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={s.valign ?? null} aria-label="Vertical alignment"
              onChange={(_, v: SoaVAlign | null) => set('valign', v ?? undefined)}>
              <ToggleButton value="top" aria-label="Top"><VerticalAlignTopIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="middle" aria-label="Middle"><VerticalAlignCenterIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="bottom" aria-label="Bottom"><VerticalAlignBottomIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <ColorField label="Colour" value={s.color} optional fallback="#000000" onChange={(v) => set('color', v)} />
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <NumField label="Line height" value={s.lineHeight} allowEmpty min={0.8} max={3} step={0.05} width={100} onChange={(v) => set('lineHeight', v)} />
            <NumField label="Spacing" unit="pt" value={s.letterSpacing} allowEmpty min={-2} max={10} step={0.1} width={100} onChange={(v) => set('letterSpacing', v)} />
            <FormControl size="small" sx={{ width: 110 }}>
              <InputLabel>Direction</InputLabel>
              <Select label="Direction" value={s.direction ?? 'auto'} onChange={(e) => set('direction', e.target.value === 'auto' ? undefined : (e.target.value as 'ltr' | 'rtl'))}>
                <MenuItem value="auto">Auto</MenuItem>
                <MenuItem value="ltr">Left to right</MenuItem>
                <MenuItem value="rtl">Right to left</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </>
      )}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <ColorField label="Fill" value={s.background || undefined} optional fallback="#f2f2f2" onChange={(v) => set('background', v)} />
        <NumField label="Padding" unit="mm" value={s.padding} allowEmpty min={0} max={20} step={0.1} width={110} onChange={(v) => set('padding', v)} />
      </Stack>
      <Box>
        <Typography variant="caption" color="text.secondary">Borders</Typography>
        <BordersEditor value={s.borders} onChange={(b) => set('borders', b)} />
      </Box>
    </Stack>
  );
};

// ── field placeholders ───────────────────────────────────────────────────────────────────────

interface InsertFieldButtonProps {
  fields: SoaFieldDef[];
  formats: Array<{ value: string; label: string; types: SoaFieldType[] }>;
  onInsert: (text: string) => void;
  /** row fields are table columns; elements use the others */
  includeRow?: boolean;
  label?: string;
}

/** "Insert field": picks a field and a format and inserts {{key|format}}. */
export const InsertFieldButton: React.FC<InsertFieldButtonProps> = ({ fields, formats, onInsert, includeRow = false, label = 'Insert field' }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [field, setField] = useState<SoaFieldDef | null>(null);
  const [format, setFormat] = useState('');
  const options = useMemo(
    () => fields
      .filter((f) => (includeRow || f.group !== 'row') && f.type !== 'image')
      .sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group)),
    [fields, includeRow],
  );
  const fitting = formats.filter((f) => !field || f.types.includes(field.type));
  return (
    <>
      <Tooltip title="Insert a field placeholder such as {{bp.name}}">
        <Button size="small" startIcon={<DataObjectIcon />} onClick={(e) => setAnchor(e.currentTarget)}>{label}</Button>
      </Tooltip>
      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Stack spacing={1.25} sx={{ p: 1.5, width: 330 }}>
          <Autocomplete
            size="small"
            options={options}
            groupBy={(o) => GROUP_LABELS[o.group] ?? o.group}
            getOptionLabel={(o) => `${o.label} (${o.key})`}
            value={field}
            onChange={(_, v) => { setField(v); setFormat(''); }}
            renderInput={(p) => <TextField {...p} label="Field" autoFocus />}
          />
          <FormControl size="small">
            <InputLabel>Format</InputLabel>
            <Select label="Format" value={format} onChange={(e) => setFormat(String(e.target.value))}>
              {fitting.map((f) => <MenuItem key={f.value || 'none'} value={f.value}>{f.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button size="small" onClick={() => setAnchor(null)}>Cancel</Button>
            <Button size="small" variant="contained" disabled={!field}
              onClick={() => {
                if (field) onInsert(placeholder(field.key, format || undefined));
                setAnchor(null);
              }}>
              Insert
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

/** Inserts text at the caret of a text box (the end when it has no caret). */
export function insertAtCaret(el: HTMLInputElement | HTMLTextAreaElement | null, current: string, text: string): string {
  if (!el || el.selectionStart === null || el.selectionEnd === null) return current + text;
  return current.slice(0, el.selectionStart) + text + current.slice(el.selectionEnd);
}

export const SmallIconButton: React.FC<{ label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode; color?: 'default' | 'error' | 'primary' }> = ({
  label, onClick, disabled, children, color = 'default',
}) => (
  <Tooltip title={label}>
    <span>
      <IconButton size="small" aria-label={label} onClick={onClick} disabled={disabled} color={color}>{children}</IconButton>
    </span>
  </Tooltip>
);
